import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { colonyApi } from "@/lib/colonyApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { useMgmtBrand } from "@/lib/useMgmtBrand";

/**
 * Unified Management USA NJ "Forgot your password?" page.
 *
 * The `/managementusa` sign-in serves three identities (admin/secretary,
 * worker, colony contact), and each of those has its OWN reset flow
 * that mints a token in its own store and mails a link pointing at
 * its own reset page:
 *   - admin  → /api/mgmt/auth/forgot-password    → /managementusa/reset-password
 *   - worker → /api/worker/auth/forgot-password  → /worker/reset-password
 *   - colony → /api/colony/auth/forgot-password  → /colonies/accept-invite
 *
 * A single "forgot" click therefore has to fan out to all three. Each
 * endpoint safely returns `{ok:true}` regardless of whether it found a
 * matching account (no email enumeration leak), so firing them all in
 * parallel is idempotent — only the store that actually owns the
 * email will send mail. If the same email lives in multiple stores
 * (rare but possible), the user gets one email per store, each with
 * the correct branded reset link.
 */
export default function MgmtForgotPassword() {
  useMgmtBrand("Management USA NJ — Forgot password");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Fan out to every identity store — each returns {ok:true}
      // even when the email doesn't exist there, so `allSettled` is
      // just belt-and-braces against a transient outage in one store.
      await Promise.allSettled([
        api.post("/mgmt/auth/forgot-password", { email }),
        api.post("/worker/auth/forgot-password", { email }),
        colonyApi.post("/colony/auth/forgot-password", { email }),
      ]);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-black tracking-tighter" data-testid="mgmt-forgot-title">
            Management USA NJ
          </h1>
          <p className="text-sm text-slate-400 mt-1">Reset your password</p>
        </div>

        <div className="bg-slate-800/60 border border-slate-700 rounded-md p-6 shadow-xl">
          {sent ? (
            <div className="text-center space-y-3" data-testid="mgmt-forgot-sent">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-600/20 flex items-center justify-center">
                <Mail className="w-6 h-6 text-emerald-300" />
              </div>
              <h2 className="font-semibold text-lg">Check your email</h2>
              <p className="text-sm text-slate-300">
                If an account exists for <b>{email}</b>, a password-reset link has been sent.
                It&apos;s valid for 24 hours (48 hours for colony contacts).
              </p>
              <Link
                to="/managementusa"
                className="inline-block text-sm text-emerald-300 hover:text-emerald-200 underline underline-offset-4 mt-4"
                data-testid="mgmt-forgot-back-login"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="mgmt-forgot-form">
              <div>
                <h2 className="font-semibold text-lg">Forgot your password?</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Enter your Management USA NJ email — admin, worker, or colony contact — and
                  we&apos;ll send you a link to set a new password.
                </p>
              </div>
              <div>
                <Label htmlFor="mfp-email" className="text-slate-300">Email</Label>
                <Input
                  id="mfp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="bg-slate-900 border-slate-700 text-slate-50"
                  data-testid="mgmt-forgot-email"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="mgmt-forgot-submit"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>
              <div className="text-center text-sm">
                <Link
                  to="/managementusa"
                  className="text-slate-400 hover:text-slate-200"
                  data-testid="mgmt-forgot-back-login"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
